// JSON-LD components utilisant le support natif des tags <head> de React 19

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://micheltsitsok.com";

/**
 * Schema.org JSON-LD : Person
 * Permet à Google d'afficher une "carte" enrichie avec nom, métier, photo,
 * réseaux sociaux quand quelqu'un cherche le nom de la personne.
 */
export const PersonJsonLd = ({
  name = "Tsitsok Michel Berger",
  jobTitle = "Ingénieur Logiciel Senior & Consultant",
  description = "Ingénieur Logiciel Senior & Consultant en développement web, mobile et systèmes.",
  image = `${SITE_URL}/photos/michel-suit.jpg`,
  url = SITE_URL,
  sameAs = [],
  knowsAbout = [],
}) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    jobTitle,
    description,
    image,
    url,
    sameAs,
    knowsAbout,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

/**
 * Schema.org JSON-LD : Article (BlogPosting)
 * Permet à Google d'afficher l'article avec image, auteur, date dans les résultats.
 */
export const ArticleJsonLd = ({
  title,
  description,
  image,
  url,
  datePublished,
  dateModified,
  authorName = "Tsitsok Michel Berger",
  tags = [],
}) => {
  const fullImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : `${SITE_URL}/photos/michel-suit.jpg`;

  const data = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: fullImage,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    url,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Person",
      name: authorName,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Person",
      name: "Tsitsok Michel Berger",
      url: SITE_URL,
      image: {
        "@type": "ImageObject",
        url: `${SITE_URL}/photos/michel-suit.jpg`,
      },
    },
    keywords: tags.join(", "),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

/**
 * Schema.org JSON-LD : Reviews + AggregateRating
 * Permet à Google d'afficher les étoiles ⭐ et le nombre d'avis dans les résultats.
 * Important: Les reviews doivent être d'utilisateurs réels et vérifiables.
 */
export const ReviewsJsonLd = ({
  testimonials = [],
  itemName = "Tsitsok Michel Berger",
  itemUrl = SITE_URL,
}) => {
  if (!testimonials || testimonials.length === 0) return null;

  const ratings = testimonials.map((t) => t.rating || 5);
  const avgRating =
    ratings.reduce((a, b) => a + b, 0) / ratings.length;

  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: itemName,
    url: itemUrl,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating.toFixed(1),
      reviewCount: testimonials.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: t.name,
      },
      datePublished: t.created_at,
      reviewBody: t.content,
      reviewRating: {
        "@type": "Rating",
        ratingValue: t.rating || 5,
        bestRating: 5,
        worstRating: 1,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

/**
 * Schema.org JSON-LD : WebSite avec SearchAction
 */
export const WebSiteJsonLd = ({ url = SITE_URL, name = "Tsitsok Michel Berger" }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url,
    potentialAction: {
      "@type": "SearchAction",
      target: `${url}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};
